import type { ProfileRoleDetails, ProfileTenant, UserModel } from '../../auth/core/_models'
import type { Vessel } from '../../Management/core/_models'

function firstPositiveId(...candidates: unknown[]): number | undefined {
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && n > 0) return n
  }
  return undefined
}

/** Numeric id from user profile (not role-id based). */
export function isCrewUser(
  user?: UserModel | null,
  roleDetails?: ProfileRoleDetails | null
): boolean {
  const token = String(
    roleDetails?.effectiveBehaviorType ??
      user?.role?.roleType ??
      user?.role?.roleName ??
      user?.role?.code ??
      ''
  ).toUpperCase()
  return token === 'CREW'
}

export function getUserVesselId(
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): number | undefined {
  const id = firstPositiveId(
    user?.vessel?.id,
    tenant?.vessel?.id,
    (tenant as any)?.vesselId,
    user?.vesselId,
    (user as any)?.roleEntity?.vessel?.id,
    (user as any)?.crew?.vesselId
  )
  return id
}

export function getUserVesselName(
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): string | undefined {
  const value =
    user?.vessel?.fleet_name ??
    (user?.vessel as any)?.name ??
    tenant?.vessel?.name ??
    (tenant as any)?.vesselName ??
    (user as any)?.crew?.vesselName

  const normalized = typeof value === 'string' ? value.trim() : ''
  return normalized || undefined
}

export function getUserCompanyAdminId(
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): number | undefined {
  const id = firstPositiveId(
    user?.companyAdminId,
    user?.companyAdmin?.id,
    user?.vessel?.companyAdminId,
    user?.vessel?.companyAdmin?.id,
    tenant?.company?.id
  )
  return id
}

export function getUserCompanyGroupAdminId(
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): number | undefined {
  if (isCrewUser(user)) {
    return firstPositiveId(
      user?.companyGroupAdminId,
      user?.companyGroupAdmin?.id,
      getTenantCompanyGroupAdminId(tenant),
      (user as any)?.vessel?.companyGroupAdminId,
      (user as any)?.vessel?.companyGroupAdmin?.id,
      (user as any)?.vessel?.companyAdmin?.cgaid?.id,
      (user as any)?.crew?.companyGroupAdminId,
      (user as any)?.crew?.companyGroupAdmin?.id
    )
  }

  const id = firstPositiveId(
    user?.companyGroupAdminId,
    user?.companyGroupAdmin?.id,
    (user as any)?.vessel?.companyGroupAdminId,
    (user as any)?.vessel?.companyGroupAdmin?.id,
    getTenantCompanyGroupAdminId(tenant)
  )
  return id
}

export function getTenantCompanyGroupAdminId(tenant?: ProfileTenant | null): number | undefined {
  const id =
    tenant?.companyGroup?.id ??
    (tenant as any)?.companyGroupId ??
    (tenant as any)?.companyGroupAdminId ??
    undefined
  return typeof id === 'number' && Number.isFinite(id) ? id : undefined
}

export function getTenantVesselId(tenant?: ProfileTenant | null): number | undefined {
  const id = tenant?.vessel?.id ?? (tenant as any)?.vesselId
  return typeof id === 'number' && Number.isFinite(id) && id > 0 ? id : undefined
}

export function getResolvedQhseCompanyScope(
  user?: UserModel | null,
  tenant?: ProfileTenant | null,
  roleDetails?: ProfileRoleDetails | null
): {
  vesselId?: number
  companyAdminId?: number
  companyGroupAdminId?: number
} {
  return {
    vesselId: getUserVesselId(user, tenant),
    companyAdminId: getUserCompanyAdminId(user, tenant),
    companyGroupAdminId: getUserCompanyGroupAdminId(user, tenant),
  }
}

export function resolveDocumentLibraryCompanyGroupId(args: {
  user?: UserModel | null
  tenant?: ProfileTenant | null
  roleDetails?: ProfileRoleDetails | null
  fleetPicker?: boolean
  selectedCompanyId?: string | number | null
}): number | undefined {
  const {user, tenant, roleDetails, fleetPicker = false, selectedCompanyId} = args

  if (isCrewUser(user, roleDetails)) {
    return getUserCompanyGroupAdminId(user, tenant)
  }

  if (fleetPicker) {
    const selected =
      typeof selectedCompanyId === 'string' && selectedCompanyId.trim() !== ''
        ? Number(selectedCompanyId)
        : typeof selectedCompanyId === 'number'
          ? selectedCompanyId
          : NaN
    return Number.isFinite(selected) ? selected : undefined
  }

  return getUserCompanyGroupAdminId(user, tenant) ?? getTenantCompanyGroupAdminId(tenant)
}

/**
 * User appears tied to a single vessel in their profile — use single-vessel UX (hide org/vessel pickers).
 * Not authorization; backend remains SoT.
 */
export function isLikelyVesselAssignedUser(
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): boolean {
  return getUserVesselId(user, tenant) != null
}

/**
 * No company-group, sub-company, or vessel anchor on profile — fleet-wide picker UX (still backend-scoped).
 */
export function isBroadFleetPickerUser(
  user?: UserModel | null,
  tenant?: ProfileTenant | null,
  roleDetails?: ProfileRoleDetails | null
): boolean {
  if (isCrewUser(user, roleDetails)) {
    return false
  }
  return (
    getUserVesselId(user, tenant) == null &&
    getUserCompanyAdminId(user, tenant) == null &&
    getUserCompanyGroupAdminId(user, tenant) == null
  )
}

/** Narrow full vessel list to what the user's profile suggests they work with. Backend APIs still enforce. */
export function filterVesselsForManagementList(
  vessels: Vessel[],
  user?: UserModel | null,
  tenant?: ProfileTenant | null
): Vessel[] {
  const vid = getUserVesselId(user, tenant)
  if (vid != null) {
    return vessels.filter((v: any) => Number(v.id) === vid && (v?.active ?? true))
  }
  const caId = getUserCompanyAdminId(user, tenant)
  if (caId != null) {
    return vessels.filter((v: any) => {
      const isActive = v?.active ?? true
      const vCa = Number(v.companyAdmin?.id ?? v.companyId)
      return isActive && vCa === caId
    })
  }
  const cgaId = getUserCompanyGroupAdminId(user, tenant)
  if (cgaId != null) {
    return vessels.filter((v: any) => {
      const isActive = v?.active ?? true
      const vCga = Number(v.companyGroupAdmin?.id ?? v.companyGroupId)
      return isActive && vCga === cgaId
    })
  }
  return vessels.filter((v: any) => v?.active ?? true)
}
